// One import file: rejected → validation errors; previewed → summary + plan grouped by change type,
// then Confirm / Cancel (admin) with an in-page confirmation step.

import { useState } from 'react';
import { api, type ChangeType, type ImportRecord, type PlanItem } from '../api.ts';
import { Icon } from '../components/Icon.tsx';
import { Card, Empty, ErrorBox, InlineError, JsonBlock, Loading, Pill, StatusPill } from '../components/ui.tsx';
import { useAction, useLoad } from '../hooks.ts';
import { href } from '../router.ts';
import { useSession } from '../session.tsx';
import { useStreamEvent } from '../stream.tsx';
import { fmtBytes, fmtLocal } from '../time.ts';

const CHANGE_ORDER: ChangeType[] = ['create', 'update', 'reactivate', 'delete', 'unchanged'];
const CHANGE_LABEL: Record<ChangeType, string> = {
  create: 'Creates',
  update: 'Updates',
  reactivate: 'Reactivates',
  delete: 'Deletes',
  unchanged: 'Unchanged',
};

function Errors({ r }: { r: ImportRecord }) {
  return (
    <Card title={<><Icon name="alert" />Validation errors<span className="n hot">{r.errors.length}</span></>}>
      <div className="note">The whole file was rejected; nothing changed. Fix these in the ERP export or the import builder and upload again.</div>
      <div className="tw">
        <table>
          <thead>
            <tr>
              <th scope="col" className="num">Entity #</th>
              <th scope="col">Type</th>
              <th scope="col">ID</th>
              <th scope="col">Path</th>
              <th scope="col">Message</th>
            </tr>
          </thead>
          <tbody>
            {r.errors.map((e, i) => (
              <tr key={i}>
                <td className="mono num">{e.entityIndex ?? '—'}</td>
                <td>{e.entityType ?? '—'}</td>
                <td className="mono">{e.entityId ?? '—'}</td>
                <td className="mono small">{e.path ?? '—'}</td>
                <td className="bad-t">{e.message}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </Card>
  );
}

function PlanGroup({ change, items }: { change: ChangeType; items: PlanItem[] }) {
  const [open, setOpen] = useState(change !== 'unchanged' && items.length <= 300);
  const holds = items.filter((i) => i.hold).length;
  const warns = items.filter((i) => i.warnings.length).length;
  return (
    <div className={`plan-g ch-${change}`}>
      <button type="button" className="plan-h" onClick={() => setOpen((o) => !o)} aria-expanded={open}>
        <span className="caret" aria-hidden="true">
          {open ? '▾' : '▸'}
        </span>
        <span className={`chg ch-${change}`}>{CHANGE_LABEL[change]}</span>
        <span className="n">{items.length}</span>
        {holds > 0 && <Pill tone="warn">{holds} held</Pill>}
        {warns > 0 && <Pill tone="warn">{warns} with warnings</Pill>}
      </button>
      {open && (
        <div className="tw">
          <table>
            <thead>
              <tr>
                <th scope="col">Type</th>
                <th scope="col">Entity</th>
                <th scope="col">Site</th>
                <th scope="col">Version</th>
                <th scope="col">Hold</th>
                <th scope="col">Warnings</th>
              </tr>
            </thead>
            <tbody>
              {items.map((p) => (
                <tr key={`${p.entityType}:${p.entityId}`} className={p.hold ? 'held' : ''}>
                  <td>{p.entityType}</td>
                  <td className="mono">{p.entityId}</td>
                  <td>{p.siteId ? <a href={href.site(p.siteId)} className="mono">{p.siteId}</a> : <span className="dim">—</span>}</td>
                  <td className="mono nowrap">
                    {p.fromVersion ?? '∅'} → {p.toVersion ?? '∅'}
                  </td>
                  <td>
                    {p.hold ? (
                      <>
                        <Pill tone="warn">{p.hold.rule}</Pill>
                        <div className="note">{p.hold.detail}</div>
                      </>
                    ) : (
                      <span className="dim">—</span>
                    )}
                  </td>
                  <td className="small">
                    {p.warnings.length ? (
                      p.warnings.map((w, i) => (
                        <div key={i} className="warn-t">
                          {w}
                        </div>
                      ))
                    ) : (
                      <span className="dim">—</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function Decision({ r, onDone }: { r: ImportRecord; onDone: (r: ImportRecord) => void }) {
  const [step, setStep] = useState<'idle' | 'confirm' | 'cancel'>('idle');
  const act = useAction(async (what: 'confirm' | 'cancel') => {
    const res = what === 'confirm' ? await api.confirmImport(r.id) : await api.cancelImport(r.id);
    setStep('idle');
    onDone(res);
    return res;
  });
  const s = r.summary;
  if (step === 'idle') {
    return (
      <div className="decide">
        <button type="button" className="btn pri" onClick={() => setStep('confirm')}>
          <Icon name="check" />
          Confirm import
        </button>
        <button type="button" className="btn" onClick={() => setStep('cancel')}>
          <Icon name="x" />
          Cancel import
        </button>
        <InlineError error={act.error} />
      </div>
    );
  }
  return (
    <div className={`confirmbar big ${step === 'cancel' ? 'danger' : ''}`} role="group" aria-label="Confirm decision">
      {step === 'confirm' ? (
        <span>
          Apply <b>{s?.creates ?? 0}</b> creates, <b>{s?.updates ?? 0}</b> updates, <b>{s?.deletes ?? 0}</b> deletes and <b>{s?.reactivates ?? 0}</b> reactivates to the replica?
          {s && s.holds > 0 ? ` ${s.holds} change${s.holds === 1 ? '' : 's'} will wait as held changes for a supervisor.` : ''}
        </span>
      ) : (
        <span>Cancel this import? Nothing in it will be applied; you can upload the file again later.</span>
      )}
      <span className="sp" />
      <button type="button" className={`btn ${step === 'confirm' ? 'pri' : 'danger'}`} disabled={act.busy} onClick={() => void act.run(step)} autoFocus>
        {act.busy ? 'Working…' : step === 'confirm' ? 'Yes, apply' : 'Yes, cancel import'}
      </button>
      <button type="button" className="btn" disabled={act.busy} onClick={() => setStep('idle')}>
        Back
      </button>
      <InlineError error={act.error} />
    </div>
  );
}

export function ImportDetailScreen({ id }: { id: string }) {
  const { isAdmin } = useSession();
  const load = useLoad((signal) => api.importRecord(id, signal), [id]);
  useStreamEvent('import', (e) => {
    if (e.id === id) load.reload();
  });
  const r = load.data;

  if (load.error && !r) {
    return (
      <div className="page">
        <a href={href.imports()} className="back">
          <Icon name="back" />
          Imports
        </a>
        <ErrorBox error={load.error} onRetry={load.reload} title="Could not load the import" />
      </div>
    );
  }
  if (!r) {
    return (
      <div className="page">
        <Loading label="Loading import…" />
      </div>
    );
  }

  const plan = r.plan ?? [];
  const groups = CHANGE_ORDER.map((c) => ({ change: c, items: plan.filter((p) => p.change === c) })).filter((g) => g.items.length);
  const s = r.summary;

  return (
    <div className="page">
      <a href={href.imports()} className="back">
        <Icon name="back" />
        Imports
      </a>
      <div className="page-h">
        <div>
          <h2>
            {r.fileName} <StatusPill status={r.status} />
          </h2>
          <p>
            Uploaded by {r.uploadedBy} · {fmtLocal(r.uploadedAt)} · {fmtBytes(r.size)} · source {r.source}
          </p>
        </div>
        <span className="sp" />
        <button type="button" className="btn" onClick={load.reload} disabled={load.loading}>
          <Icon name="sync" />
          Refresh
        </button>
      </div>
      {load.error ? <ErrorBox error={load.error} onRetry={load.reload} title="Refresh failed; showing earlier data" /> : null}

      <div className="fgrid">
        <div className="ro">
          <span className="lbl">Import ID</span>
          <span className="mono small">{r.id}</span>
        </div>
        <div className="ro">
          <span className="lbl">File ID</span>
          <span className="mono small">{r.fileId}</span>
        </div>
        <div className="ro">
          <span className="lbl">SHA-256</span>
          <span className="mono small brk">{r.sha256}</span>
        </div>
        <div className="ro">
          <span className="lbl">Scope</span>
          <JsonBlock value={r.scope} maxHeight={120} />
        </div>
        {r.confirmedBy && (
          <div className="ro">
            <span className="lbl">Confirmed</span>
            <span>
              {r.confirmedBy} · {fmtLocal(r.appliedAt)}
            </span>
          </div>
        )}
      </div>

      {r.status === 'rejected' && (r.errors.length ? <Errors r={r} /> : <ErrorBox error={new Error('The file was rejected without error details.')} title="Rejected" />)}

      {s && (
        <div className="tiles">
          <div className="tilek ch-create"><b>{s.creates}</b><small>Creates</small></div>
          <div className="tilek ch-update"><b>{s.updates}</b><small>Updates</small></div>
          <div className="tilek ch-delete"><b>{s.deletes}</b><small>Deletes</small></div>
          <div className="tilek ch-reactivate"><b>{s.reactivates}</b><small>Reactivates</small></div>
          <div className="tilek"><b>{s.unchanged}</b><small>Unchanged</small></div>
          <div className={`tilek${s.holds ? ' warnk' : ''}`}><b>{s.holds}</b><small>Held for a supervisor</small></div>
          <div className={`tilek${s.warnings ? ' warnk' : ''}`}><b>{s.warnings}</b><small>Warnings</small></div>
        </div>
      )}

      {r.status === 'previewed' &&
        (isAdmin ? (
          <Decision r={r} onDone={(res) => load.setData((cur) => ({ ...res, plan: res.plan ?? cur?.plan }))} />
        ) : (
          <div className="note">
            <Icon name="lock" /> An admin confirms or cancels this import.
          </div>
        ))}
      {r.status === 'applying' && <Loading label="Applying… this page updates when it finishes." />}
      {r.status === 'applied' && (
        <div className="banner ok">
          <Icon name="check" />
          <span>
            Applied{r.appliedAt ? ` ${fmtLocal(r.appliedAt)}` : ''}{r.confirmedBy ? ` by ${r.confirmedBy}` : ''}. Held changes wait on the <a href={href.imports()}>Imports</a> screen.
          </span>
        </div>
      )}

      {r.status !== 'rejected' && (
        <Card title={<><Icon name="list" />Plan</>}>
          {groups.length === 0 ? (
            <Empty>{r.plan ? 'The plan is empty: nothing in this file changes the replica.' : 'No plan is available for this import.'}</Empty>
          ) : (
            groups.map((g) => <PlanGroup key={g.change} change={g.change} items={g.items} />)
          )}
        </Card>
      )}
    </div>
  );
}
